// The weekly scheduled eval on the Evals overview (TOM-142, ADR 0268): the
// on/off switch, the next run, what a run launches, where alerts go, and the
// last fire with its run and its alert. Turning it on asks once more, since
// each run spends up to its budget. The server audits every change. Every time
// reads in the viewer's own zone, the stored UTC slot beside it (TOM-139).

import { useEffect, useState } from 'react';

import { adminApiMessage, evalsApi } from '../adminApi';
import { EvalBadge, RunLink } from './EvalBits';
import { statusLabel } from './format';
import {
  alertStateText,
  alertsText,
  deliveryText,
  lastFireText,
  launchesText,
  localDateTimeText,
  localSlotText,
  nextRunText,
  reasonsText,
  slotShiftText,
  utcSlotText,
} from './scheduleView';
import type { EvalScheduleView } from './types';

export function EvalSchedulePanel() {
  const [view, setView] = useState<EvalScheduleView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmOn, setConfirmOn] = useState(false);

  useEffect(() => {
    let ignore = false;
    evalsApi
      .schedule()
      .then((res) => {
        if (!ignore) setView(res);
      })
      .catch((err) => {
        if (!ignore) setError(adminApiMessage(err, 'Failed to load the weekly schedule.'));
      });
    return () => {
      ignore = true;
    };
  }, []);

  async function setEnabled(enabled: boolean) {
    setBusy(true);
    setError(null);
    try {
      setView(await evalsApi.updateSchedule({ enabled }));
      setConfirmOn(false);
    } catch (err) {
      setError(adminApiMessage(err, 'The change failed.'));
    } finally {
      setBusy(false);
    }
  }

  const last = view?.lastFire ?? null;
  const local = view !== null ? localSlotText(view.nextSlot) : '';
  const shift = view !== null ? slotShiftText(view.nextSlot) : null;
  return (
    <div className="eval-schedule" data-testid="eval-schedule">
      <h2>
        Weekly scheduled run{' '}
        {view !== null ? (
          view.enabled ? (
            <EvalBadge tone="succeeded" testId="eval-schedule-state">
              On
            </EvalBadge>
          ) : (
            <EvalBadge tone="muted" testId="eval-schedule-state">
              Off
            </EvalBadge>
          )
        ) : null}
      </h2>
      {error !== null ? (
        <p className="eval-error-text" role="alert">
          {error}
        </p>
      ) : null}
      {view === null && error === null ? <p className="admin-loading-row">Loading…</p> : null}
      {view !== null ? (
        <>
          <dl className="admin-defs">
            <dt>When</dt>
            <dd data-testid="eval-schedule-when">
              {local}
              {/* The stored UTC slot, unless the viewer's zone is UTC already. */}
              {local.endsWith(' UTC') ? null : (
                <>
                  {' '}
                  <span className="admin-muted eval-nowrap">
                    ({utcSlotText(view.weekday, view.hourUtc)})
                  </span>
                </>
              )}
              {shift !== null ? <div className="admin-muted eval-sub">{shift}</div> : null}
            </dd>
            <dt>Next</dt>
            <dd data-testid="eval-schedule-next">{nextRunText(view)}</dd>
            <dt>Runs</dt>
            <dd>{launchesText(view)}</dd>
            <dt>Alerts</dt>
            <dd>{alertsText(view)}</dd>
            <dt>Last fire</dt>
            <dd data-testid="eval-schedule-last">
              {lastFireText(last)}
              {last?.run != null ? (
                <span>
                  {' '}
                  <RunLink id={last.run.id} /> · {statusLabel(last.run.status)} ·{' '}
                  {alertStateText(last.run.alertState)}
                </span>
              ) : null}
              {last?.regrade != null ? (
                <div className="eval-sub">
                  Its judge failed, so it was re-graded: <RunLink id={last.regrade.id} /> ·{' '}
                  {statusLabel(last.regrade.status)} · {alertStateText(last.regrade.alertState)}
                </div>
              ) : null}
              {last?.alert != null ? (
                <div className="admin-muted eval-sub">
                  Alert {deliveryText(last.alert.delivery, last.alert.deliveryDetail)}:{' '}
                  {reasonsText(last.alert.reasons)}. “{last.alert.subject}”
                </div>
              ) : null}
            </dd>
            {view.updatedBy !== null ? (
              <>
                <dt>Changed</dt>
                <dd>
                  {localDateTimeText(view.updatedAt)} by {view.updatedBy}
                </dd>
              </>
            ) : null}
          </dl>
          {view.enabled ? (
            <button
              type="button"
              className="admin-button-secondary"
              disabled={busy}
              onClick={() => void setEnabled(false)}
              data-testid="eval-schedule-off"
            >
              Turn off
            </button>
          ) : confirmOn ? (
            <div className="admin-confirm">
              <span className="admin-confirm-prompt">
                Turn on the weekly run? Each run can spend up to $
                {view.launches.budgetUsd.toFixed(2)} of provider money, inside the daily eval cap.
              </span>
              <button
                type="button"
                className="admin-button-affirm"
                disabled={busy}
                onClick={() => void setEnabled(true)}
                data-testid="eval-schedule-confirm-on"
              >
                Turn on
              </button>
              <button
                type="button"
                className="admin-link-button"
                onClick={() => setConfirmOn(false)}
              >
                Keep off
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="admin-button-secondary"
              onClick={() => setConfirmOn(true)}
              data-testid="eval-schedule-on"
            >
              Turn on…
            </button>
          )}
        </>
      ) : null}
    </div>
  );
}
